import React, { useEffect, useRef, useState } from 'react';
import './Home.css';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3500';

interface DetailItem {
  name: string;
  value: number | null;
  source: string | null;
  column?: string;
  sign?: number;
  after?: string;
  file?: string;
  duplicate_of?: string[];
  sub_details?: { value: number | null; source: string | null }[];
}

interface CellData {
  value: number | null;
  source?: string | null;
  details?: DetailItem[];
  bold?: boolean;
  file?: string;
  account?: string;
  group?: string;
  column?: string;
}

interface OverallRow {
  name: string;
  cells: CellData[];
}

interface OverallTable {
  name: string;
  rows: OverallRow[];
}

interface SelectedCell {
  rowName: string;
  label: string;
  cell: CellData;
}

interface ColumnDef {
  config: string;
  cluster: string;
  label: string;
}

export default function Overall({ apiKey }: { apiKey: string }) {
  const [labels, setLabels] = useState<string[]>([]);
  const [tables, setTables] = useState<OverallTable[]>([]);
  const [openTables, setOpenTables] = useState<Set<number>>(() => {
    try {
      const saved = localStorage.getItem('overallDefaultOpen');
      if (saved) return new Set(JSON.parse(saved) as number[]);
    } catch {}
    return new Set([0]);
  });
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState<SelectedCell | null>(null);
  const [refreshing, setRefreshing] = useState(false);
  const [cachedAt, setCachedAt] = useState<string | null>(null);
  const [fromCache, setFromCache] = useState<boolean>(false);
  const [scrollLeftByTable, setScrollLeftByTable] = useState<Record<number, number>>({});
  // Сходимость расшифровок по каждой колонке — считает бэкенд по правилу из конфига.
  const [columnChecks, setColumnChecks] = useState<{ label: string; checks: { table: string;
    anchor: string[]; anchor_value: number; table_sum: number; diff: number; ok: boolean;
    incomplete: boolean }[] }[]>([]);

  // Esc закрывает панель детализации — иначе её можно закрыть только мышью.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setSelected(null); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  // Edit mode
  const [editing, setEditing] = useState(false);
  const [columns, setColumns] = useState<ColumnDef[]>([]);
  const [clusters, setClusters] = useState<string[]>([]);
  const [configs, setConfigs] = useState<string[]>([]);
  const [newCluster, setNewCluster] = useState('');
  const [newConfig, setNewConfig] = useState('');
  const [newLabel, setNewLabel] = useState('');
  const [saving, setSaving] = useState(false);

  // Settings dropdown
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [months, setMonths] = useState<string>('');
  const [savingSettings, setSavingSettings] = useState(false);

  // Source: '' = configured columns, otherwise a group id
  const [groups, setGroups] = useState<{ id: string; name: string }[]>([]);
  const [selectedGroup, setSelectedGroup] = useState<string>('');
  // Источник, которому принадлежат данные НА ЭКРАНЕ. Пока он не равен selectedGroup, таблица
  // показывает чужие цифры — состояние хранит и данные, и их происхождение, поэтому UI не может
  // приписать их не тому источнику. null = ещё ничего не загружено.
  const [loadedSource, setLoadedSource] = useState<string | null>(null);
  // Запросы к /overall?group=... идут по 6 с и не кэшируются: при переключении источника два
  // ответа гонятся, и раньше в состояние писал тот, кто пришёл последним. Счётчик отсекает
  // устаревшие ответы, AbortController рвёт ненужное соединение.
  const reqIdRef = useRef(0);
  const abortRef = useRef<AbortController | null>(null);

  const headers = { 'X-API-Key': apiKey };

  const sourceName = (src: string | null) => {
    if (src === null) return '';
    if (src === '') return 'Настроенные колонки';
    return groups.find((g) => g.id === src)?.name ?? src;
  };

  const loadReport = (source: string) => {
    const id = ++reqIdRef.current;
    abortRef.current?.abort();
    const ctrl = new AbortController();
    abortRef.current = ctrl;
    setLoading(true);
    const url = source
      ? `${API_URL}/overall?group=${encodeURIComponent(source)}`
      : `${API_URL}/overall`;
    fetch(url, { headers, signal: ctrl.signal })
      .then((res) => res.json())
      .then((data) => {
        if (id !== reqIdRef.current) return;  // ответ устарел — пользователь уже сменил источник
        setLabels(data.labels || []);
        setTables(data.tables || []);
        setCachedAt(data.computed_at || null);
        setColumnChecks(data.column_checks || []);
        setFromCache(Boolean(data.cached));
        setLoadedSource(source);
      })
      .catch(() => {})
      .finally(() => {
        if (id === reqIdRef.current) setLoading(false);
      });
  };

  const hardRefresh = () => {
    setRefreshing(true);
    fetch(`${API_URL}/overall/refresh`, { method: 'POST', headers })
      .then(() => loadReport(selectedGroup))
      .finally(() => setRefreshing(false));
  };

  useEffect(() => {
    fetch(`${API_URL}/groups`, { headers })
      .then((r) => r.json())
      .then((d) => setGroups((d.groups || []).map((g: { id: string; name: string }) => ({ id: g.id, name: g.name }))))
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiKey]);

  useEffect(() => {
    loadReport(selectedGroup);
    // Уход со страницы во время 6-секундного пересчёта не должен оставлять висящий запрос.
    return () => abortRef.current?.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiKey, selectedGroup]);

  const enterEditMode = () => {
    Promise.all([
      fetch(`${API_URL}/overall/columns`, { headers }).then((r) => r.json()),
      fetch(`${API_URL}/clusters`, { headers }).then((r) => r.json()),
      fetch(`${API_URL}/configs`, { headers }).then((r) => r.json()),
    ]).then(([colData, clusterData, configData]) => {
      setColumns(colData.columns || []);
      setClusters(clusterData.clusters || []);
      setConfigs(configData.configs || []);
      setEditing(true);
    });
  };

  const removeColumn = (idx: number) => {
    setColumns((prev) => prev.filter((_, i) => i !== idx));
  };

  const addColumn = () => {
    if (!newCluster || !newConfig || !newLabel.trim()) return;
    setColumns((prev) => [...prev, { cluster: newCluster, config: newConfig, label: newLabel.trim() }]);
    setNewCluster('');
    setNewConfig('');
    setNewLabel('');
  };

  const openSettings = () => {
    fetch(`${API_URL}/overall/settings`, { headers })
      .then((r) => r.json())
      .then((data) => {
        const s = data.settings || {};
        setMonths(s.months !== undefined && s.months !== null ? String(s.months) : '');
        setSettingsOpen(true);
      });
  };

  const saveSettings = () => {
    setSavingSettings(true);
    const payload: Record<string, unknown> = {};
    const trimmed = months.trim();
    if (trimmed !== '') {
      const n = Number(trimmed);
      if (!Number.isNaN(n)) payload.months = n;
    }
    fetch(`${API_URL}/overall/settings`, {
      method: 'PUT',
      headers: { ...headers, 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    })
      .then(() => {
        setSettingsOpen(false);
        loadReport(selectedGroup);
      })
      .finally(() => setSavingSettings(false));
  };

  const saveColumns = () => {
    setSaving(true);
    fetch(`${API_URL}/overall/columns`, {
      method: 'PUT',
      headers: { ...headers, 'Content-Type': 'application/json' },
      body: JSON.stringify(columns),
    })
      .then(() => {
        setEditing(false);
        loadReport(selectedGroup);
      })
      .finally(() => setSaving(false));
  };

  const formatNumber = (val: number | null) => {
    if (val === null) return '—';
    return val.toLocaleString('ru-RU');
  };

  const toggleTable = (ti: number) => {
    setOpenTables((prev) => {
      const next = new Set(prev);
      if (next.has(ti)) next.delete(ti);
      else next.add(ti);
      return next;
    });
  };

  const toggleDefault = (ti: number, checked: boolean) => {
    setOpenTables((prev) => {
      const next = new Set(prev);
      if (checked) next.add(ti);
      else next.delete(ti);
      localStorage.setItem('overallDefaultOpen', JSON.stringify(Array.from(next)));
      return next;
    });
  };

  // Hover highlight: track which cell (column index) is hovered on a formula row
  const [hoverInfo, setHoverInfo] = useState<{ colIdx: number; highlights: Map<string, number> } | null>(null);

  const handleCellHover = (cell: CellData, colIdx: number) => {
    if (!cell.bold || !cell.details || cell.details.length === 0) {
      setHoverInfo(null);
      return;
    }
    const highlights = new Map<string, number>();
    for (const d of cell.details) {
      highlights.set(d.name, d.sign ?? 1);
    }
    setHoverInfo({ colIdx, highlights });
  };

  // Данные на экране принадлежат другому источнику, чем выбран в селекторе.
  const isStale = loadedSource !== null && loadedSource !== selectedGroup;

  const handleCellClick = (rowName: string, label: string, cell: CellData) => {
    if (isStale) return;  // детализация относилась бы к прошлому источнику
    if (cell.value === null && !cell.source) return;
    setSelected({ rowName, label, cell });
  };

  // --- Сверка расшифровок с СГД ---------------------------------------------------------
  // Каждая расшифровка должна в сумме давать строку расходов из СГД: таблица ТБ — «Расходы по
  // ТБ», таблица 1C — «Административные расходы». Конфиги без таблицы 1C (dd, ea, kkg) держат
  // обе статьи в одной таблице ТБ, поэтому для таких колонок эталон — сумма двух строк.
  const sgdRow = (name: string) =>
    tables[0]?.rows.find((r) => r.name === name);
  const tbExpense = sgdRow('Расходы по ТБ');
  const admExpense = sgdRow('Административные расходы');
  const has1cTable = tables.map((t) => t.name.includes('1C'));
  const colHas1c = labels.map((_, j) =>
    tables.some((t, ti) => has1cTable[ti] && t.rows.some((r) => r.cells[j]?.value !== null && r.cells[j]?.value !== undefined)),
  );

  // Итог расшифровки для конкретной колонки — чтобы показать его прямо под строкой
  // «Расходы по ТБ» / «Административные расходы», с которой он обязан совпадать.
  const checkFor = (rowName: string, j: number) =>
    columnChecks[j]?.checks.find((c) => c.anchor.includes(rowName));

  const expectedFor = (ti: number, j: number): number | null => {
    const tb = tbExpense?.cells[j]?.value ?? null;
    const adm = admExpense?.cells[j]?.value ?? null;
    if (tb === null && adm === null) return null;
    if (colHas1c[j]) return (has1cTable[ti] ? adm : tb) ?? 0;
    return (tb ?? 0) + (adm ?? 0);
  };

  return (
    <div className="report report-wide">
      <div className="overall-toolbar">
        {!editing ? (
          <div className="overall-toolbar-buttons">
            <label style={{ display: 'flex', alignItems: 'center', gap: 6, marginRight: 4 }}>
              <span style={{ color: 'var(--text-muted)', fontSize: 14 }}>Источник:</span>
              <select value={selectedGroup} onChange={(e) => setSelectedGroup(e.target.value)} style={{ padding: '6px 8px' }}>
                <option value="">Настроенные колонки</option>
                {groups.map((g) => (
                  <option key={g.id} value={g.id}>Группа: {g.name}</option>
                ))}
              </select>
            </label>
            <button className="overall-edit-btn" onClick={enterEditMode} disabled={!!selectedGroup} title={selectedGroup ? 'Доступно только для настроенных колонок' : ''}>Настроить колонки</button>
            <div className="overall-settings-wrap">
              <button className="overall-edit-btn" onClick={() => (settingsOpen ? setSettingsOpen(false) : openSettings())}>
                ⚙ Настройки
              </button>
              {settingsOpen && (
                <div className="overall-settings-panel">
                  <label className="overall-settings-row">
                    <span>Длительность отчёта, мес.</span>
                    <input
                      type="number"
                      step="1"
                      min="0"
                      placeholder="например, 12"
                      value={months}
                      onChange={(e) => setMonths(e.target.value)}
                    />
                  </label>
                  <div className="overall-settings-actions">
                    <button onClick={saveSettings} disabled={savingSettings}>
                      {savingSettings ? 'Сохранение...' : 'Сохранить'}
                    </button>
                    <button onClick={() => setSettingsOpen(false)}>Отмена</button>
                  </div>
                </div>
              )}
            </div>
            <button className="overall-edit-btn" onClick={hardRefresh} disabled={refreshing} title="Пересчитать (сбросить кэш)">
              {refreshing ? 'Пересчёт...' : '↻ Пересчитать'}
            </button>
            <button className="overall-edit-btn" onClick={() => {
              const link = document.createElement('a');
              const params = new URLSearchParams({ api_key: apiKey });
              if (selectedGroup) params.set('group', selectedGroup);
              link.href = `${API_URL}/overall/export?${params.toString()}`;
              link.click();
            }}>Скачать Excel</button>
            {cachedAt && (
              <span className="overall-cache-info">
                {fromCache ? 'из кэша' : 'свежий расчёт'} · {new Date(cachedAt).toLocaleString('ru-RU')}
              </span>
            )}
          </div>
        ) : (
          <div className="overall-editor">
            <h3>Колонки отчёта</h3>
            <div className="overall-columns-list">
              {columns.map((col, i) => (
                <div key={i} className="overall-column-item">
                  <span className="overall-column-label">{col.label}</span>
                  <span className="overall-column-meta">{col.cluster} / {col.config}</span>
                  <button className="cluster-delete" onClick={() => removeColumn(i)} title="Удалить">&times;</button>
                </div>
              ))}
            </div>
            <div className="overall-add-row">
              <select value={newCluster} onChange={(e) => setNewCluster(e.target.value)}>
                <option value="">Кластер...</option>
                {clusters.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
              <select value={newConfig} onChange={(e) => setNewConfig(e.target.value)}>
                <option value="">Конфиг...</option>
                {configs.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
              <input
                type="text"
                placeholder="Название"
                value={newLabel}
                onChange={(e) => setNewLabel(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && addColumn()}
              />
              <button onClick={addColumn} disabled={!newCluster || !newConfig || !newLabel.trim()}>+</button>
            </div>
            <div className="overall-editor-actions">
              <button onClick={saveColumns} disabled={saving}>{saving ? 'Сохранение...' : 'Сохранить'}</button>
              <button onClick={() => setEditing(false)}>Отмена</button>
            </div>
          </div>
        )}
        {loading && <span className="loading-indicator">Загрузка...</span>}
      </div>

      {/* Сходится ли расшифровка со строкой СГД — по каждой компании сразу, без ручного счёта. */}
      {columnChecks.length > 0 && !isStale && (
        <div className="ov-checks">
          <span className="ov-checks-title">Сверка расшифровок:</span>
          {columnChecks.map((cc) => {
            const bad = cc.checks.filter((c) => !c.ok && !c.incomplete);
            const unknown = cc.checks.filter((c) => c.incomplete);
            const state = bad.length ? 'is-bad' : unknown.length ? 'is-unknown' : 'is-ok';
            const title = cc.checks.length
              ? cc.checks
                  .map((c) => `${c.table}: ${c.ok ? 'сходится'
                    : c.incomplete ? 'нет данных для сверки'
                    : `расхождение ${c.diff.toLocaleString('ru-RU', { maximumFractionDigits: 2 })}`}`)
                  .join('\n')
              : 'для этого конфига правило сверки не задано';
            return (
              <span key={cc.label} className={`ov-check ${state}`} title={title}>
                <span className="ov-check-mark">{bad.length ? '!' : unknown.length ? '?' : '✓'}</span>
                {cc.label}
                {bad.length > 0 && (
                  <span className="ov-check-diff">
                    {bad
                      .map((c) => c.diff.toLocaleString('ru-RU', { maximumFractionDigits: 0 }))
                      .join(' / ')}
                  </span>
                )}
              </span>
            );
          })}
        </div>
      )}

      {isStale && (
        <div className="overall-stale-banner">
          <span className="overall-stale-spinner">⟳</span>
          <span>
            Считается <b>{sourceName(selectedGroup)}</b>. Ниже — данные за <b>{sourceName(loadedSource)}</b>,
            они ещё не обновились.
          </span>
        </div>
      )}

      <div className={isStale ? 'overall-tables is-stale' : 'overall-tables'}>
      {tables.map((table, ti) => {
        const tableWidth = 280 + (labels.length + 1) * 170;
        return (
        <div key={ti} className="overall-table-block">
          <div className="overall-sticky-header" style={{ width: tableWidth }}>
            <div className="overall-sticky-header-name" onClick={() => toggleTable(ti)}>
              <input
                type="checkbox"
                className="table-default-checkbox"
                checked={openTables.has(ti)}
                onClick={(e) => e.stopPropagation()}
                onChange={(e) => toggleDefault(ti, e.target.checked)}
                title="Открыт по умолчанию"
              />
              <span className="table-toggle-arrow">{openTables.has(ti) ? '▼' : '▶'}</span>
              {table.name}
            </div>
            <div className="overall-sticky-header-scroll">
              <div
                className="overall-sticky-header-cols"
                style={{
                  width: (labels.length + 1) * 170,
                  transform: `translateX(-${scrollLeftByTable[ti] || 0}px)`,
                }}
              >
                {labels.map((label) => (
                  <div key={label} className="overall-sticky-header-col">{label}</div>
                ))}
                <div className="overall-sticky-header-col">Итого</div>
              </div>
            </div>
          </div>
          {openTables.has(ti) && (
            <div
              className="report-table-scroll"
              onScroll={(e) => {
                const nextLeft = e.currentTarget.scrollLeft;
                setScrollLeftByTable((prev) => (
                  prev[ti] === nextLeft ? prev : { ...prev, [ti]: nextLeft }
                ));
              }}
            >
            <table
              className="report-table"
              style={{ width: tableWidth, minWidth: tableWidth }}
            >
            <colgroup>
              <col style={{ width: 280 }} />
              {labels.map((label) => (
                <col key={label} style={{ width: 170 }} />
              ))}
              <col style={{ width: 170 }} />
            </colgroup>
            <tbody>
              {table.rows.map((row, i) => {
                const highlightSign = hoverInfo?.highlights.get(row.name);
                const rowTotal = row.cells.reduce((sum, c) => sum + (c.value || 0), 0);
                return (
                <tr key={i} className={
                  highlightSign !== undefined
                    ? highlightSign > 0 ? 'row-highlight-plus' : 'row-highlight-minus'
                    : ''
                }>
                  <td>{row.name}</td>
                  {row.cells.map((cell, j) => (
                    <td
                      key={j}
                      className={`num${cell.source ? ' cell-clickable' : ''}${cell.bold ? ' cell-formula' : ''}`}
                      onClick={() => handleCellClick(row.name, labels[j], cell)}
                      onMouseEnter={() => handleCellHover(cell, j)}
                      onMouseLeave={() => setHoverInfo(null)}
                    >
                      {formatNumber(cell.value)}
                      {(() => {
                        const c = checkFor(row.name, j);
                        if (!c) return null;
                        return (
                          <span
                            className={`cell-check${c.ok ? ' is-ok' : ' is-bad'}`}
                            title={`${c.table}: ${formatNumber(c.table_sum)}${c.ok ? ' — совпадает'
                              : `, расхождение ${formatNumber(c.diff)}`}${c.anchor.length > 1
                              ? `\nсверяется с суммой строк: ${c.anchor.join(' + ')}` : ''}`}
                          >
                            {/* У конфигов без таблицы 1C расшифровка сверяется с суммой двух
                                строк СГД — помечаем Σ, иначе число под ячейкой выглядит
                                как несовпадение. */}
                            {c.anchor.length > 1 ? 'Σ ' : ''}{formatNumber(c.table_sum)}
                          </span>
                        );
                      })()}
                    </td>
                  ))}
                  <td className="num"><strong>{formatNumber(rowTotal)}</strong></td>
                </tr>
                );
              })}
              {table.name.includes('Расшифровка') && (
                <tr className="row-bold overall-totals-row">
                  <td>Итого</td>
                  {labels.map((_, j) => {
                    const total = table.rows.reduce((sum, row) => sum + (row.cells[j]?.value || 0), 0);
                    return <td key={j} className="num">{formatNumber(total)}</td>;
                  })}
                  <td className="num">{formatNumber(table.rows.reduce((sum, row) => sum + row.cells.reduce((s, c) => s + (c.value || 0), 0), 0))}</td>
                </tr>
              )}
              {table.name.includes('Расшифровка') && (
                <tr className="overall-recon-row">
                  <td title="Итого расшифровки минус соответствующая строка расходов в СГД">
                    Сверка с СГД
                  </td>
                  {labels.map((_, j) => {
                    const hasData = table.rows.some((row) => row.cells[j]?.value !== null && row.cells[j]?.value !== undefined);
                    const expected = expectedFor(ti, j);
                    if (!hasData || expected === null) return <td key={j} className="num recon-empty">—</td>;
                    const total = table.rows.reduce((sum, row) => sum + (row.cells[j]?.value || 0), 0);
                    const diff = total - expected;
                    const ok = Math.abs(diff) < 0.01;
                    return (
                      <td
                        key={j}
                        className={`num ${ok ? 'recon-ok' : 'recon-bad'}`}
                        title={`расшифровка ${formatNumber(Math.round(total))} − СГД ${formatNumber(Math.round(expected))}`}
                      >
                        {ok ? '✓' : formatNumber(Math.round(diff))}
                      </td>
                    );
                  })}
                  <td className="num recon-empty">—</td>
                </tr>
              )}
            </tbody>
            </table>
            </div>
          )}
        </div>
        );
      })}
      </div>

      {selected && (() => {
        const cell = selected.cell;
        const details = cell.details || [];
        const signed = (d: any) => (d.value || 0) * (d.sign ?? 1);
        const isMissing = (d: any) => typeof d.source === 'string' && d.source.includes('not found');
        const found = details.filter((d: any) => !isMissing(d));
        const missing = details.filter(isMissing);
        const sum = details.reduce((a: number, d: any) => a + signed(d), 0);
        const maxAbs = Math.max(1, ...details.map((d: any) => Math.abs(signed(d))));
        const matches = cell.value === null || Math.abs(sum - (cell.value || 0)) < 0.01;
        const isFormula = typeof cell.source === 'string' && cell.source.startsWith('расчёт');
        const share = (d: any) => (cell.value ? (signed(d) / cell.value) * 100 : 0);

        return (
        <>
          <div className="panel-overlay" onClick={() => setSelected(null)} />
          <div className="detail-panel detail-panel-wide">
            <div className="detail-panel-header">
              <h3>{selected.rowName}</h3>
              <span className="detail-panel-label">{selected.label}</span>
              <button className="detail-panel-close" onClick={() => setSelected(null)} title="Закрыть (Esc)">✕</button>
            </div>

            <div className="detail-panel-value">{formatNumber(cell.value)}</div>

            {details.length > 0 && (
              <div className="dv-summary">
                Складывается из {details.length}{' '}
                {details.length === 1 ? 'источника' : 'источников'}
                {missing.length > 0 && (
                  <> · <span className="dv-warn">не найдено: {missing.length}</span></>
                )}
              </div>
            )}

            {isFormula && (
              <div className="dv-formula"><strong>Формула:</strong> {cell.source?.replace('расчёт: ', '')}</div>
            )}

            {(cell.file || cell.account || cell.group || cell.column) && (
              <div className="detail-panel-meta">
                {cell.file && <span><strong>Файл:</strong> {cell.file}</span>}
                {cell.account && <span><strong>Счёт:</strong> {cell.account}</span>}
                {cell.group && <span><strong>Группа:</strong> {cell.group}</span>}
                {cell.column && <span><strong>Колонка:</strong> {cell.column}</span>}
              </div>
            )}

            {!details.length && cell.source && !isFormula && (
              <div className="detail-panel-source"><strong>Источник:</strong> {cell.source}</div>
            )}

            {/* Карточками, а не таблицей: в узкой панели пять колонок со статьями и адресами
                ячеек сжимались в нечитаемую кашу. */}
            {found.length > 0 && (
              <div className="dv-list">
                {found.map((d: any, i: number) => {
                  const v = signed(d);
                  return (
                    <div key={i} className={`dv-item${v < 0 ? ' is-negative' : ''}`}>
                      <div className="dv-item-head">
                        <span className="dv-item-name">
                          {d.name}
                          {d.duplicate_of && (
                            <span className="detail-dup" title={`ячейка ${d.duplicate_of.join(', ')} уже посчитана другим лукапом этой строки`}>дубль</span>
                          )}
                        </span>
                        <span className="dv-item-value">{v < 0 ? '−' : ''}{formatNumber(Math.abs(v))}</span>
                      </div>
                      <div className="dv-bar">
                        <span style={{ width: `${(Math.abs(v) / maxAbs) * 100}%` }} />
                      </div>
                      <div className="dv-item-meta">
                        <span className="dv-chip">{d.file || '—'}</span>
                        {d.source && <span className="dv-chip">{String(d.source).split(', ').slice(-1)[0]}</span>}
                        {d.column && <span className="dv-chip">{d.column}</span>}
                        {d.after && <span className="dv-chip dv-chip-soft">после: {d.after}</span>}
                        {cell.value ? <span className="dv-share">{share(d).toFixed(1)}%</span> : null}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {missing.length > 0 && (
              <div className="dv-missing">
                <div className="dv-missing-title">Не найдено в файлах ({missing.length})</div>
                {missing.map((d: any, i: number) => (
                  <div key={i} className="dv-missing-item">
                    <span className="dv-item-name">{d.name}</span>
                    <span className="dv-missing-src">{d.source}</span>
                  </div>
                ))}
                <div className="dv-missing-hint">
                  Обычно это значит, что файл не загружен или статья переименована в выгрузке 1С.
                </div>
              </div>
            )}

            {details.length > 0 && (
              <div className={`dv-total${matches ? ' is-ok' : ' is-bad'}`}>
                <span>Сумма составляющих</span>
                <span className="dv-total-value">{formatNumber(sum)}</span>
                <span className="dv-total-mark">{matches ? '✓ совпадает' : '! расходится'}</span>
              </div>
            )}
          </div>
        </>
        );
      })()}
    </div>
  );
}
